/**
 * Job timeline (Phase 65 / BLU-594 PR3b). Created → started → finished dots.
 * Reached stamps show emerald/purple filled dots; pending stamps a hollow dot.
 */
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { DATE_TIME_FORMAT, useDateFnsLocale } from '@/lib/format-dates';

interface Stamp {
  labelKey: string;
  at: string | null;
}

export function JobTimeline({ createdAt, startedAt, finishedAt }: { createdAt: string; startedAt: string | null; finishedAt: string | null }) {
  const { t } = useTranslation('productSync');
  const locale = useDateFnsLocale();
  const stamps: Stamp[] = [
    { labelKey: 'job.timeline.created', at: createdAt },
    { labelKey: 'job.timeline.started', at: startedAt },
    { labelKey: 'job.timeline.finished', at: finishedAt },
  ];

  return (
    <ol className="space-y-2.5">
      {stamps.map((s, i) => {
        const reached = s.at != null;
        return (
          <li key={s.labelKey} className="flex items-start gap-2.5">
            <span className="mt-0.5 flex flex-col items-center">
              <span
                className={cn('h-2.5 w-2.5 rounded-full ring-2', reached ? 'bg-threadheads-purple ring-threadheads-purple/25' : 'bg-transparent ring-border')}
                aria-hidden="true"
              />
              {i < stamps.length - 1 && <span className="mt-0.5 h-5 w-px bg-border" aria-hidden="true" />}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(s.labelKey)}</span>
              <span className="text-xs tabular-nums text-foreground">
                {reached ? format(new Date(s.at!), DATE_TIME_FORMAT, { locale }) : t('job.timeline.pending')}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
